/**
 * PayPlus Stage B — generate payment page link for an existing pending booking.
 * Callback (`POST /bookings/payplus/callback`) is server-only; frontend never calls it.
 */
import { requestAssetApi, type RequestAssetApiDebugSnapshot } from './getAssetById';

export type PayplusGenerateLinkBody = {
  booking_id: string;
  customer_name: string;
  email: string;
  phone?: string;
  refURL_success: string;
  refURL_failure: string;
  refURL_cancel: string;
};

export type PayplusGenerateLinkResult = {
  payment_page_link: string;
  page_request_uid: string | null;
  qr_code_image: string | null;
};

export type PayplusApiCallOptions = {
  onRequestDebug?: (snapshot: RequestAssetApiDebugSnapshot) => void;
};

function pickString(raw: unknown): string | null {
  if (typeof raw !== 'string') {
    return null;
  }
  const t = raw.trim();
  return t.length > 0 ? t : null;
}

/** Normalize generate-link JSON (snake_case or camelCase). */
export function parsePayplusGenerateLinkResponse(raw: unknown): PayplusGenerateLinkResult {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new Error('PayPlus generate-link returned an empty response.');
  }
  const row = raw as Record<string, unknown>;
  const payment_page_link = pickString(
    row.payment_page_link ?? row.paymentPageLink ?? row.payment_link ?? row.paymentLink,
  );
  if (!payment_page_link) {
    throw new Error('PayPlus generate-link response missing payment_page_link.');
  }
  return {
    payment_page_link,
    page_request_uid: pickString(row.page_request_uid ?? row.pageRequestUid),
    qr_code_image: pickString(row.qr_code_image ?? row.qrCodeImage),
  };
}

export async function postPayplusGenerateLink(
  body: PayplusGenerateLinkBody,
  opts?: PayplusApiCallOptions,
): Promise<PayplusGenerateLinkResult> {
  const bookingId = body.booking_id.trim();
  if (!bookingId) {
    throw new Error('booking_id is required.');
  }
  const customerName = body.customer_name.trim();
  if (!customerName) {
    throw new Error('customer_name is required.');
  }
  const email = body.email.trim();
  if (!email) {
    throw new Error('email is required.');
  }
  const payload: Record<string, string> = {
    booking_id: bookingId,
    customer_name: customerName,
    email,
    refURL_success: body.refURL_success.trim(),
    refURL_failure: body.refURL_failure.trim(),
    refURL_cancel: body.refURL_cancel.trim(),
  };
  const phone = body.phone?.trim();
  if (phone) {
    payload.phone = phone;
  }
  const raw = await requestAssetApi({
    method: 'POST',
    path: '/bookings/payplus/generate-link',
    body: payload,
    onRequestDebug: opts?.onRequestDebug,
  });
  return parsePayplusGenerateLinkResponse(raw);
}
